import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import gregorian from 'react-date-object/calendars/gregorian';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import client from '../../api/client';
import { toLatinDigits, toPersianDigits } from '../../utils/jalali';

const EMPTY_FORM = { name: '', start_date: '', end_date: '', date_limit: '' };

const PAGE_SIZES = [10, 25, 50];

// Long enough that a burst of typing costs one request, short enough that the
// list still feels like it is following along.
const SEARCH_DEBOUNCE_MS = 300;

// The API speaks Gregorian "YYYY-MM-DD" end to end, so the form state stays
// Gregorian and Jalali exists only at the picker/table boundary. The string has
// to be parsed as Gregorian first -- handing it straight to the Persian
// calendar would read 2025 as a Jalali year.
const toJalali = (isoDate) => new DateObject({
  date: isoDate, format: 'YYYY-MM-DD', calendar: gregorian,
}).convert(persian, persian_fa);

// gregorian_en matters as much as the calendar here: converting without it
// keeps the Persian locale and formats as "۲۰۲۶-۰۸-۰۹", which the API rejects.
const toISO = (dateObject) => dateObject.convert(gregorian, gregorian_en).format('YYYY-MM-DD');

const fmtDate = (isoDate) => (isoDate ? toJalali(isoDate).format('YYYY/MM/DD') : '—');

// The two bounds-cache columns need different handling, and collapsing them
// into one helper reintroduces the bug this fixes.
//
// min_date/max_date are naive `timestamp`: no offset, so the string is the
// intended wall clock and must be read verbatim. Parsing it as a real instant
// would let the browser zone shift it across a day boundary.
const fmtCacheDate = (value) => (value
  ? new DateObject({
    date: String(value).slice(0, 19).replace('T', ' '),
    format: 'YYYY-MM-DD HH:mm:ss',
    calendar: gregorian,
  }).convert(persian, persian_fa).format('YYYY/MM/DD HH:mm')
  : '—');

// last_refreshed_at is `timestamptz` and arrives as UTC, so it has to go
// through Date for the browser to convert it. Reading it verbatim showed the
// UTC wall clock, which in Tehran left it 3:30 behind -- a refresh that had
// just run looked hours stale, as if the button had done nothing.
const fmtStamp = (value) => (value
  ? new DateObject({ date: new Date(value), calendar: gregorian })
    .convert(persian, persian_fa).format('YYYY/MM/DD HH:mm:ss')
  : '—');

const CalendarPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [cache, setCache] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ busy: true, error: null, message: null });

  // `search` is what is in the box; `query` is what the server has been asked
  // about. Keeping them apart is what lets the request lag the typing.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  // The API hides admins by default -- they are not normally policy members.
  // Off by default here too so the picker keeps showing the usual roster.
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [usersBusy, setUsersBusy] = useState(true);
  const latestUserRequest = useRef(0);

  const fail = useCallback((error, fallback) => setStatus({
    busy: false,
    message: null,
    error: error?.response?.data?.detail || fallback,
  }), []);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Normalised on the way out so searching a phone with Persian digits
      // reaches the API as the ASCII form the column actually stores.
      setQuery(toLatinDigits(search).trim());
      // A narrower term has fewer pages than the one before it, so holding on
      // to the page number would drop the user onto an empty list. Reset here
      // beside setQuery rather than from an effect watching it: React batches
      // the pair into one render, so the list is fetched once, not twice.
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = useCallback(async () => {
    const requestId = latestUserRequest.current + 1;
    latestUserRequest.current = requestId;
    setUsersBusy(true);
    try {
      const { data } = await client.get('admin/users', {
        params: {
          q: query || undefined, page, page_size: pageSize, include_admins: includeAdmins,
        },
      });
      // A slow answer to an earlier keystroke must not overwrite the list with
      // results for a term the user has already typed past.
      if (latestUserRequest.current !== requestId) return;
      setUsers({ items: data.items, total: data.total });
    } finally {
      if (latestUserRequest.current === requestId) setUsersBusy(false);
    }
  }, [query, page, pageSize, includeAdmins]);

  // Policies and the bounds cache do not depend on the search, so they are
  // fetched apart from the user page -- otherwise every keystroke would drag
  // both of them along.
  const loadShared = useCallback(async () => {
    const [p, c] = await Promise.all([
      client.get('admin/calendar-policies'),
      client.get('admin/bounds-cache'),
    ]);
    setPolicies(p.data);
    setCache(c.data);
  }, []);

  useEffect(() => {
    loadShared()
      .then(() => setStatus((s) => ({ ...s, busy: false })))
      .catch((error) => fail(error, 'خطا در دریافت اطلاعات'));
  }, [loadShared, fail]);

  useEffect(() => {
    loadUsers().catch((error) => fail(error, 'خطا در دریافت کاربران'));
  }, [loadUsers, fail]);

  const run = async (fn, message) => {
    setStatus((s) => ({ ...s, busy: true, error: null, message: null }));
    try {
      await fn();
      await Promise.all([loadShared(), loadUsers()]);
      setStatus({ busy: false, error: null, message });
    } catch (error) {
      setStatus({
        busy: false,
        message: null,
        error: error?.response?.data?.detail || 'عملیات ناموفق بود',
      });
    }
  };

  const createPolicy = () => run(async () => {
    await client.post('admin/calendar-policies', {
      name: form.name,
      start_date: form.start_date,
      // Empty inputs mean "inherit": no end date follows the automatic ceiling,
      // no limit falls back to the global one.
      end_date: form.end_date || null,
      date_limit: form.date_limit ? Number(form.date_limit) : null,
    });
    setForm(EMPTY_FORM);
  }, 'بازه ساخته شد');

  const deletePolicy = (id) => run(
    () => client.delete(`admin/calendar-policies/${id}`),
    'بازه حذف شد',
  );

  const assignUsers = () => run(async () => {
    await client.put(`admin/calendar-policies/${selectedPolicy}/members`, {
      user_ids: selectedUsers,
    });
    setSelectedUsers([]);
  }, 'کاربران به بازه اختصاص یافتند');

  const removeMember = (policyId, userId) => run(
    () => client.delete(`admin/calendar-policies/${policyId}/members/${userId}`),
    'کاربر از بازه خارج شد',
  );

  const saveCacheSettings = (patch) => run(
    () => client.patch('admin/bounds-cache', patch),
    'تنظیمات ذخیره شد',
  );

  const refreshNow = () => run(
    () => client.post('admin/bounds-cache/refresh'),
    'بازه‌ی داده به‌روزرسانی شد',
  );

  const toggleUser = (userId) => setSelectedUsers((prev) => (
    prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
  ));

  // Selection is held as ids rather than rows, so it survives paging and
  // searching: an admin can filter to one team, tick them, search the next and
  // tick those too, then assign the whole lot in one go.
  const pageIds = useMemo(() => users.items.map((u) => u.user_id), [users.items]);
  const allOnPageSelected = pageIds.length > 0
    && pageIds.every((id) => selectedUsers.includes(id));

  const togglePage = () => setSelectedUsers((prev) => (allOnPageSelected
    ? prev.filter((id) => !pageIds.includes(id))
    : [...new Set([...prev, ...pageIds])]));

  const pageCount = Math.max(1, Math.ceil(users.total / pageSize));

  // Deleting a policy does not change the user count, but a page can still be
  // left stranded past the end -- clamp rather than show an empty table.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    // Page chrome (padding, width, heading) now belongs to AdminPanel, which
    // hosts this as one tab among several.
    <div dir="rtl" className="text-right">
      {status.error && (
        <div className="mb-4 rounded bg-red-100 text-red-800 p-3">{status.error}</div>
      )}
      {status.message && (
        <div className="mb-4 rounded bg-green-100 text-green-800 p-3">{status.message}</div>
      )}

      {/* ---- automatic bounds ------------------------------------------- */}
      <section className="mb-8 bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">بازه‌ی خودکار (از روی داده)</h2>
        {cache && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
              <div><span className="text-gray-500">از:</span> {fmtCacheDate(cache.min_date)}</div>
              <div><span className="text-gray-500">تا:</span> {fmtCacheDate(cache.max_date)}</div>
              <div><span className="text-gray-500">آخرین بررسی:</span> {fmtStamp(cache.last_refreshed_at)}</div>
              <div><span className="text-gray-500">مدت اجرا:</span> {cache.last_duration_ms ?? '—'} ms</div>
            </div>
            {cache.last_error && (
              <div className="mb-3 rounded bg-amber-100 text-amber-900 p-2 text-sm">
                آخرین خطا: {cache.last_error}
              </div>
            )}
            <div className="flex flex-wrap gap-3 items-end">
              <label className="text-sm">
                <span className="block mb-1">فاصله‌ی بررسی (دقیقه)</span>
                <input
                  type="number" min="1" max="10080" defaultValue={cache.refresh_interval_minutes}
                  className="border rounded p-2 w-32"
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v !== cache.refresh_interval_minutes) {
                      saveCacheSettings({ refresh_interval_minutes: v });
                    }
                  }}
                />
              </label>
              <label className="text-sm">
                <span className="block mb-1">پنجره‌ی اسکن (تعداد pkid)</span>
                <input
                  type="number" min="1" defaultValue={cache.lookback_pkids}
                  className="border rounded p-2 w-40"
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v !== cache.lookback_pkids) saveCacheSettings({ lookback_pkids: v });
                  }}
                />
              </label>
              <button
                type="button" disabled={status.busy} onClick={refreshNow}
                className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50"
              >
                به‌روزرسانی فوری
              </button>
            </div>
          </>
        )}
      </section>

      {/* ---- policies ---------------------------------------------------- */}
      <section className="mb-8 bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">بازه‌های تعریف‌شده</h2>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <input
            className="border rounded p-2" placeholder="نام بازه" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <DatePicker
            calendar={persian} locale={persian_fa} calendarPosition="bottom-right"
            value={form.start_date ? toJalali(form.start_date) : ''}
            onChange={(d) => setForm({ ...form, start_date: d ? toISO(d) : '' })}
            inputClass="border rounded p-2 w-full text-right"
            containerClassName="w-full"
            placeholder="از تاریخ"
          />
          <DatePicker
            calendar={persian} locale={persian_fa} calendarPosition="bottom-right"
            value={form.end_date ? toJalali(form.end_date) : ''}
            onChange={(d) => setForm({ ...form, end_date: d ? toISO(d) : '' })}
            inputClass="border rounded p-2 w-full text-right"
            containerClassName="w-full"
            placeholder="تا تاریخ (اختیاری)"
          />
          <input
            className="border rounded p-2" type="number" min="1" placeholder="سقف روز (اختیاری)"
            value={form.date_limit}
            onChange={(e) => setForm({ ...form, date_limit: e.target.value })}
          />
          <button
            type="button" disabled={status.busy || !form.name || !form.start_date}
            onClick={createPolicy}
            className="bg-green-600 text-white rounded px-4 py-2 disabled:opacity-50"
          >
            ساخت بازه
          </button>
        </div>

        <table className="w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-right p-2">نام</th>
              <th className="text-right p-2">از</th>
              <th className="text-right p-2">تا</th>
              <th className="text-right p-2">سقف روز</th>
              <th className="text-right p-2">کاربران</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.policy_id} className="border-t">
                <td className="p-2">{p.name}</td>
                <td className="p-2">{fmtDate(p.start_date)}</td>
                <td className="p-2">{p.end_date ? fmtDate(p.end_date) : 'تا سقف خودکار'}</td>
                <td className="p-2">{p.date_limit ?? 'پیش‌فرض'}</td>
                <td className="p-2">{p.member_count}</td>
                <td className="p-2 text-left">
                  <button
                    type="button" disabled={status.busy}
                    onClick={() => deletePolicy(p.policy_id)}
                    className="text-red-600 disabled:opacity-50"
                  >
                    حذف
                  </button>
                </td>
              </tr>
            ))}
            {policies.length === 0 && (
              <tr><td colSpan="6" className="p-3 text-gray-500">هنوز بازه‌ای تعریف نشده</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {/* ---- assignment -------------------------------------------------- */}
      <section className="bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold mb-3">اختصاص بازه به کاربران</h2>

        <div className="flex flex-wrap gap-3 items-center mb-4">
          <select
            className="border rounded p-2" value={selectedPolicy || ''}
            onChange={(e) => setSelectedPolicy(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— انتخاب بازه —</option>
            {policies.map((p) => (
              <option key={p.policy_id} value={p.policy_id}>{p.name}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={status.busy || !selectedPolicy || selectedUsers.length === 0}
            onClick={assignUsers}
            className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50"
          >
            اختصاص به {toPersianDigits(selectedUsers.length)} کاربر
          </button>
          {selectedUsers.length > 0 && (
            <button
              type="button" onClick={() => setSelectedUsers([])}
              className="text-gray-500 text-sm underline"
            >
              پاک کردن انتخاب
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-center mb-3">
          <div className="relative flex-1 min-w-64">
            <input
              className="border rounded p-2 w-full"
              placeholder="جستجوی نام، نام خانوادگی یا شماره موبایل"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button" onClick={() => setSearch('')}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 px-1"
              >
                ✕
              </button>
            )}
          </div>
          <label className="text-sm text-gray-500">
            نمایش
            <select
              className="border rounded p-2 mx-2" value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>{toPersianDigits(n)}</option>
              ))}
            </select>
            در هر صفحه
          </label>
          {/* Toggling this changes `total`, so the page has to go back to one:
              the admins-hidden list can be several pages longer than the one
              that comes back, which would leave an empty page on screen. */}
          <label className="text-sm text-gray-500 flex items-center gap-2">
            <input
              type="checkbox" checked={includeAdmins}
              onChange={(e) => { setIncludeAdmins(e.target.checked); setPage(1); }}
            />
            نمایش ادمین‌ها
          </label>
        </div>

        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="p-2">
                  <input
                    type="checkbox" checked={allOnPageSelected}
                    disabled={pageIds.length === 0} onChange={togglePage}
                    title="انتخاب همه‌ی این صفحه"
                  />
                </th>
                <th className="text-right p-2">نام</th>
                <th className="text-right p-2">موبایل</th>
                <th className="text-right p-2">بازه‌ی فعلی</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody className={usersBusy ? 'opacity-50' : ''}>
              {users.items.map((u) => (
                <tr key={u.user_id} className="border-t">
                  <td className="p-2">
                    <input
                      type="checkbox" checked={selectedUsers.includes(u.user_id)}
                      onChange={() => toggleUser(u.user_id)}
                    />
                  </td>
                  <td className="p-2">
                    {u.name} {u.family_name}
                    {u.is_admin && (
                      <span className="mr-2 text-xs rounded px-1.5 py-0.5 bg-amber-100 text-amber-700">
                        ادمین
                      </span>
                    )}
                  </td>
                  <td className="p-2">{toPersianDigits(u.phone_number)}</td>
                  <td className="p-2">{u.policy_name || 'خودکار'}</td>
                  <td className="p-2 text-left">
                    {u.policy_id && (
                      <button
                        type="button" disabled={status.busy}
                        onClick={() => removeMember(u.policy_id, u.user_id)}
                        className="text-red-600 disabled:opacity-50"
                      >
                        حذف بازه
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.items.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-3 text-gray-500">
                    {usersBusy && 'در حال دریافت…'}
                    {!usersBusy && (query ? 'کاربری با این مشخصات پیدا نشد' : 'کاربری موجود نیست')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm">
          <span className="text-gray-500">
            {toPersianDigits(users.total)} کاربر
            {selectedUsers.length > 0
              && ` — ${toPersianDigits(selectedUsers.length)} انتخاب‌شده`}
          </span>
          <div className="flex items-center gap-2">
            {/* Worded rather than arrowed: an arrow glyph in an RTL page points
                the opposite way to what "next" means here. */}
            <button
              type="button" disabled={page <= 1} onClick={() => setPage(1)}
              className="border rounded px-3 py-1 disabled:opacity-40"
            >
              اول
            </button>
            <button
              type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="border rounded px-3 py-1 disabled:opacity-40"
            >
              قبلی
            </button>
            <span className="text-gray-500 px-1">
              صفحه‌ی {toPersianDigits(page)} از {toPersianDigits(pageCount)}
            </span>
            <button
              type="button" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}
              className="border rounded px-3 py-1 disabled:opacity-40"
            >
              بعدی
            </button>
            <button
              type="button" disabled={page >= pageCount} onClick={() => setPage(pageCount)}
              className="border rounded px-3 py-1 disabled:opacity-40"
            >
              آخر
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CalendarPolicies;
